import React from 'react';
import { Badge } from './ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { Lock } from 'lucide-react';
import { SO_STATUS_CONFIG } from '../constants/salesOrderStatuses';

/**
 * ReservedStockIndicator Component
 *
 * Displays reserved stock quantity with tooltip showing SO/PO details.
 * Follows the pattern from PurchaseApprovalBadge and InventoryStatusDisplay.
 *
 * @param {Object} props
 * @param {number} props.reservedQuantity - Total reserved quantity
 * @param {Array} props.reservationDetails - Array of reservation details with SO/PO info
 * @param {string} props.size - Component size: 'small' | 'medium'
 * @param {boolean} props.showIcon - Whether to show the lock icon
 * @param {boolean} props.inline - Icon-only mode for inline display next to quantity
 */
const ReservedStockIndicator = ({
  reservedQuantity = 0,
  reservationDetails = [],
  size = 'small',
  showIcon = true,
  inline = false  // icon-only mode for inline display
}) => {
  // No reserved stock - don't render anything
  if (reservedQuantity === 0) {
    return null;
  }

  // Build tooltip content
  const tooltipContent = (
    <div className="max-w-[300px]">
      <p className="font-medium mb-1">预留库存明细</p>
      <p className="text-xs text-muted-foreground mb-2">
        以下订单占用了此产品的库存:
      </p>

      {reservationDetails.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          共 {reservedQuantity} 件已预留
        </p>
      ) : (
        <div>
          {reservationDetails.map((detail, index) => (
            <div key={`${detail.so_id || detail.movement_id}-${index}`} className="mb-2">
              {index > 0 && <hr className="my-1 border-border" />}
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-xs font-medium">
                  {detail.so_id || '无关联销售单'}
                </span>
                {detail.so_status && (
                  <Badge
                    variant="outline"
                    className="h-4 px-1 py-0 text-[10px]"
                  >
                    {SO_STATUS_CONFIG[detail.so_status]?.label || detail.so_status}
                  </Badge>
                )}
              </div>
              <p className="text-xs">
                数量: <strong>{detail.quantity}</strong>
              </p>
              {detail.po_id && (
                <p className="text-xs text-muted-foreground">
                  采购单: {detail.po_id}
                </p>
              )}
            </div>
          ))}
          <hr className="my-1 border-border" />
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">总预留:</span>
            <span className="text-xs font-bold text-warning">{reservedQuantity}</span>
          </div>
        </div>
      )}
    </div>
  );

  const indicator = inline ? (
    // Inline mode: just icon, no text, minimal styling
    <Lock
      className={`text-warning cursor-help ${size === 'small' ? 'h-3.5 w-3.5' : 'h-4 w-4'}`}
    />
  ) : (
    // Full mode: icon + text with badge styling
    <div
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-warning/10 border border-warning/20 cursor-help"
    >
      {showIcon && (
        <Lock
          className={`text-warning ${size === 'small' ? 'h-3.5 w-3.5' : 'h-4 w-4'}`}
        />
      )}
      <span className="text-xs font-medium text-warning whitespace-nowrap">
        {reservedQuantity} 预留
      </span>
    </div>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {indicator}
        </TooltipTrigger>
        <TooltipContent side="top">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ReservedStockIndicator;
